import React, { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CourseProvider } from './contexts/CourseContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import PerformanceMonitor from './components/common/PerformanceMonitor';
import RouteSEO from './components/common/RouteSEO';
import DhikrWelcomePopup from './components/common/DhikrWelcomePopup';
import SentryErrorButton from './components/test/SentryErrorButton';
import { initializeMobileApp, handleAppUrl } from './utils/mobileUtils';
import GradeCoursesPage from './pages/GradeCoursesPage';
import BookGradePage from './pages/BookGradePage';
import TeacherPage from './pages/TeacherPage';
import SubjectTeachersPage from './pages/SubjectTeachersPage';
import CheckoutPage from './pages/CheckoutPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import RefundPolicy from './pages/RefundPolicy';
import ParentMonitoring from './pages/ParentMonitoring';
import ContactPage from './pages/ContactPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import NotFound from './pages/NotFound';
// Import device debug utilities (available in browser console as window.deviceDebug)
import './utils/deviceDebug';


// Pages
import Home from './pages/Home';
import ForgotPassword from './pages/ForgotPassword';

// Components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ResetPassword from './components/auth/ResetPassword';
import Profile from './components/user/Profile';
import CourseDetail from './components/course/CourseDetail';
import UnitDetail from './components/unit/UnitDetail';
import CourseViewer from './components/course/CourseViewer';
import RefreshTokenTest from './components/test/RefreshTokenTest';
import UnitViewer from './components/unit/UnitViewer';
import CourseCurriculum from './pages/CourseCurriculum';
import CourseUnitLessons from './pages/CourseUnitLessons';
import StudentPortalLayout from './components/layout/StudentPortalLayout';
import PortalBrowse from './pages/student-portal/PortalBrowse';
import PortalMyLearning from './pages/student-portal/PortalMyLearning';
import PortalWallet from './pages/student-portal/PortalWallet';
import PortalForum from './pages/student-portal/PortalForum';
import PortalTeacherPage from './pages/student-portal/PortalTeacherPage';
import PortalGrades from './pages/student-portal/PortalGrades';
import PortalMyMistakes from './pages/student-portal/PortalMyMistakes';

// Redirect authenticated students to portal on home page refresh (Login.tsx handles post-login)
const NavigationHandler: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasNavigatedRef = React.useRef(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (location.pathname === '/login' || isLoading) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isAuthenticated && location.pathname === '/' && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      timeoutRef.current = setTimeout(() => {
        navigate('/student-portal', { replace: true });
      }, 200);
    }

    if (location.pathname !== '/') {
      hasNavigatedRef.current = false;
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  return null;
};

function App() {
  useEffect(() => {
    initializeMobileApp();
    handleAppUrl();
  }, []);

  // Reveal deep links after the correct route has painted (see public/index.html await-app)
  useLayoutEffect(() => {
    document.documentElement.classList.remove('await-app');
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ThemeProvider>
            <AuthProvider>
              <CourseProvider>
                <div className="min-h-screen bg-paper night-mode-bg">
                  <RouteSEO />
                  <NavigationHandler />
                  <DhikrWelcomePopup />
                  <Routes>
                    <Route element={<MainLayout />}>
                      <Route path="/" element={<Home />} />
                      <Route path="/teacher/:teacherId/grade/:grade" element={<GradeCoursesPage />} />
                      <Route path="/teacher/:teacherId" element={<TeacherPage />} />
                      <Route path="/subject/:subject" element={<SubjectTeachersPage />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/checkout" element={<CheckoutPage />} />
                      <Route path="/payment-success" element={
                        <ProtectedRoute>
                          <PaymentSuccessPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/refund-policy" element={<RefundPolicy />} />
                      <Route path="/parent-monitoring" element={<ParentMonitoring />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/courses/grade/:grade" element={<GradeCoursesPage />} />
                      <Route path="/books/grade/:grade" element={<BookGradePage />} />
                      <Route path="/profile" element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      } />
                      <Route path="/my-lectures" element={<Navigate to="/student-portal" replace />} />
                      <Route path="/enrolled-courses" element={<Navigate to="/student-portal" replace />} />
                      <Route path="/course/:courseId/curriculum" element={
                        <ProtectedRoute>
                          <CourseCurriculum />
                        </ProtectedRoute>
                      } />
                      <Route path="/course/:courseId/curriculum/unit/:unitId" element={
                        <ProtectedRoute>
                          <CourseUnitLessons />
                        </ProtectedRoute>
                      } />
                      <Route path="/courses/:courseSlug" element={<CourseDetail />} />
                      <Route path="/units/:id" element={<UnitDetail />} />
                      <Route path="/test-refresh-token" element={
                        <ProtectedRoute>
                          <RefreshTokenTest />
                        </ProtectedRoute>
                      } />
                      <Route path="*" element={<NotFound />} />
                    </Route>
                    <Route path="/student-portal" element={
                      <ProtectedRoute>
                        <StudentPortalLayout />
                      </ProtectedRoute>
                    }>
                      <Route index element={<PortalMyLearning />} />
                      <Route path="browse" element={<PortalBrowse />} />
                      <Route path="teacher/:teacherId" element={<PortalTeacherPage />} />
                      <Route path="wallet" element={<PortalWallet />} />
                      <Route path="forum" element={<PortalForum />} />
                      <Route path="my-lessons" element={<Navigate to="/student-portal" replace />} />
                      <Route path="my-courses" element={<Navigate to="/student-portal" replace />} />
                      <Route path="my-grades" element={<PortalGrades />} />
                      <Route path="my-mistakes" element={<PortalMyMistakes />} />
                      <Route path="my-mistakes/:lessonId" element={<PortalMyMistakes />} />
                    </Route>
                    <Route path="/course-player/:courseId" element={
                      <ProtectedRoute>
                        <CourseViewer />
                      </ProtectedRoute>
                    } />
                    <Route path="/unit-viewer/:unitId" element={
                      <ProtectedRoute>
                        <UnitViewer />
                      </ProtectedRoute>
                    } />
                  </Routes>
                  <PerformanceMonitor />
                  <SentryErrorButton />
                </div>
              </CourseProvider>
            </AuthProvider>
          </ThemeProvider>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
